import { Component, Input, OnChanges, OnInit, HostListener, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NewsFeed, PanelState } from '../../../beans/dashboard/dashboard-module.bean';
import { DashboardConstants } from '../../../constants/dashboard.constants';
import { AdaptiveCardsService } from '../../../services/dashboard/adaptive-cards.service';
import { DatePipe } from '@angular/common';
import { AnalyticsBean } from 'src/app/beans/analytics/analytics-module.bean';
import { EheAnalyticsDirective } from '../../shared/ehe-analytics/ehe-analytics.directive';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { S3Service } from 'src/app/services/health-record/other-documents/s3.service';
import { decode } from 'he';
import { UpdateProviderVideoModalComponent } from '../../shared/update-provider-video-modal/update-provider-video-modal.component';
import { MatDialog } from '@angular/material/dialog';
import AdaptiveCards from '../../../../assets/scripts/libs/adaptive-cards.js';

/**
 * Interface containing data related to feeds
 */
export interface Article {
	/** holds article content */
	content: string;
	/** holds article date */
	date: string;
	/** holds d-chart info */
	dComplete: boolean;
	/** holds image details */
	image: string;
	/** holds link text */
	linkText: string;
	/** holds router link */
	routeLink?: string;
	/** holds title info */
	title: string;
}

/**
 * Feeds panel component
 */
@Component({
	selector: 'app-feeds-panel',
	templateUrl: './feeds-panel.component.html',
	styleUrls: ['./feeds-panel.component.scss'],
	providers: [DatePipe, EheAnalyticsDirective],
})
export class FeedsPanelComponent implements OnInit, OnChanges {
	/**
	 * Flag to check if member is matched or not
	 */
	@Input() isMatchedMember: boolean;
	/**
	 * Holds news feeds data
	 */
	@Input() newsFeeds: NewsFeed[];
	/**
	 * Holds profile related info
	 */
	@Input() panelStatus: PanelState;
	/**
	 * Holds feed counter value
	 */
	feedCounter = 0;
	/**
	 * Holds news feed counter value
	 */
	newsFeedCounter = 0;
	/**
	 * Holds feed responses array
	 */
	feedResponses: any = [];
	/**
	 * Holds news feed responses array
	 */
	newsFeedResponses: any = [];
	/**
	 * Holds state of user
	 */
	state: number;
	/**
	 * Holds profile related info
	 */
	profile: any = [];
	/**
	 * Holds patient name
	 */
	patientName: any;
	/**
	 * Holds template array
	 */
	templatesArray: Array<any> = [];
	/**
	 * Flag to check if card is rendered or not
	 */
	cardRendered: boolean;
	/**
	 * Holds analytics data
	 */
	analyticsData: AnalyticsBean;
	/**
	 * Flag to show/hide pdf - product update feed
	 */
	showPDF: boolean;
	/**
	 * Flag to decide if autoResize should be enabled or not
	 */
	autoResize: boolean;
	/**
	 * Holds url string
	 */
	url: any = '';

	/**
	 * Feeds panel component constructor
	 * @param route - angular route
	 * @param environment - environment specific values
	 * @param adaptiveCardsService - adaptive card service - holding config
	 * @param sanitizer - dom sanitozer
	 * @param eheAnalyticsDirective - analytics directive
	 * @param spinner - spinner component
	 * @param dataCarrier - data carrier service
	 * @param s3Service - s3 service
	 */
	constructor(
		private route: Router,
		private adaptiveCardsService: AdaptiveCardsService,
		private sanitizer: DomSanitizer,
		private eheAnalyticsDirective: EheAnalyticsDirective,
		private spinner: SpinnerComponent,
		private s3Service: S3Service,
		private dialog: MatDialog
	) {
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'FeedsPanel';
		this.analyticsData.pageUrl = '/dashboard';
		this.showPDF = false;
		this.autoResize = false;
	}

	/**
	 * Listens window size and when its less than 1024, the pdf is rendered for full screen else in the middle of screen - product update feed
	 */
	@HostListener('window:resize') onResize() {
		if (window.innerWidth < 1024) {
			this.autoResize = true;
		} else {
			this.autoResize = false;
		}
	}

	/**
	 * On component initialization
	 */
	ngOnInit() {
		if (this.newsFeeds) {
			this.setupAdaptiveCards();
		}
	}

	/**
	 * Sets newsfeeds data in adaptive card config
	 */
	setupAdaptiveCards() {
		AdaptiveCards.AdaptiveCard.onExecuteAction = (action: any) => {
			if (action instanceof AdaptiveCards.SubmitAction) {
				if (action.data) {
					if (action.data.routeURL) {
						this.analyticsData.id = action.title;
						this.analyticsData.redirectedTo = action.data.routeURL;
						this.analyticsData.placement = 'Dashboard feeds';
						this.eheAnalyticsDirective.captureAdaptiveCardButtonData(this.analyticsData);
						// newTab - 1: To be opened in a new tab
						// newTab - 2: To be opened as pdf in same tab
						// newTab - 0: Routes to be changed within angular
						if (action.data.newTab === '1') {
							window.open(action.data.routeURL, '_blank');
						} else if (action.data.newTab === '2') {
							this.s3Service.setPreSignedUrl(action.data.routeURL);
							this.s3Service.getPreSignedUrl().subscribe(
								res => {
									this.url = res;
									this.spinner.startLoader();
									this.showPDF = true;
									window.scroll(0, 0);
									document.body.classList.add('no-scroll');
								},
								err => {
									console.error(`[${FeedsPanelComponent.name}][${this.setupAdaptiveCards.name}]`, err);
								}
							);
						} else if (action.data.newTab === '3') {
							const dialogRef = this.dialog.open(UpdateProviderVideoModalComponent, {
								width: '70vw',
								panelClass: 'video-overlay',
								autoFocus: false,
								data: {
									url: action.data.routeURL,
								},
							});
						} else {
							/**
							 * For health coaching quiz feed, adding query params
							 */
							if (action.title === 'TAKE THE QUIZ') {
								this.route.navigate(['/health/mentor'], {
									queryParams: { q: 'quiz' },
								});
							} else {
								this.route.navigate([action.data.routeURL]);
							}
						}
					}
				}
			} else if (action.url) {
				this.analyticsData.id = action.title;
				this.analyticsData.redirectedTo = action.url;
				this.analyticsData.placement = 'Dashboard feeds';
				this.eheAnalyticsDirective.captureAdaptiveCardButtonData(this.analyticsData);
				this.route.navigate([action.url]);
			}
		};
	}

	/**
	 * Sets profile related info in feeds panel
	 */
	setProfileInformation() {
		if (this.panelStatus) {
			this.patientName = this.panelStatus.firstName;
			this.renderDashboardCards();
		}
	}

	/**
	 * Renders dashboard cards
	 */
	renderDashboardCards() {
		this.templatesArray = this.newsFeeds;
		this.renderFeeds();
		this.renderNewsFeeds(this.patientName);
	}

	/**
	 * On component change
	 * @param changes component changes
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (changes.newsFeeds && this.newsFeeds) {
			this.setProfileInformation();
		}
	}

	/**
	 * Renders news feeds
	 * @param patientName - current user name
	 */
	renderNewsFeeds(patientName: string) {
		const self = this;
		const feedTemplate = DashboardConstants.News_Feed_Template;
		self.newsFeeds.forEach(newsFeed => {
			const renderedHandleBar = self.adaptiveCardsService.compileHandlebars(feedTemplate, newsFeed);
			const feedElement = self.renderCard(renderedHandleBar);
			const feedWrapper = "<div id='news-feed-" + this.newsFeedCounter + "'></div>";
			self.newsFeedResponses.push({
				content: self.sanitizer.bypassSecurityTrustHtml(feedWrapper),
			});
			const id = self.newsFeedCounter;
			self.newsFeedCounter++;
			self.appendFeedToWrapper('news-feed-' + id, feedElement);
		});
	}

	/**
	 * Renders news feeds
	 */
	renderFeeds() {
		const self = this;
		if (self.templatesArray) {
			self.templatesArray.forEach(template => {
				const hasImage = this.hasImageInTemplate(template);
				const imageURL = template.image as string;
				if (hasImage) {
					template.image = imageURL;
				}
				template.image = '#';
				const data = self.adaptiveCardsService.loadFeedTemplate(template);
				// Initializing adaptive card object
				let feedElement = this.renderCard(data);
				const feedWrapper = "<div id='feed-" + this.feedCounter + "'></div>";
				self.feedResponses.push({
					content: self.sanitizer.bypassSecurityTrustHtml(feedWrapper),
				});
				const id = this.feedCounter;
				this.feedCounter++;
				if (hasImage) {
					feedElement = this.updateImageCardBeforeRender(feedElement, imageURL);
				}
				self.appendFeedToWrapper('feed-' + id, feedElement);
			});
		}
	}

	/**
	 * Renders adaptive card
	 * @param feedTemplate - feed template
	 */
	renderCard(feedTemplate: any) {
		feedTemplate = decode(feedTemplate);
		const adaptiveFeed = new AdaptiveCards.AdaptiveCard();

		// Setting host config (Styles json) for adaptiveFeed
		adaptiveFeed.hostConfig = DashboardConstants.AdaptiveCard_Config;
		adaptiveFeed.parse(JSON.parse(feedTemplate));
		// Rendering the Feed
		const feedElement = adaptiveFeed.render();
		return feedElement;
	}

	/**
	 * appending apadtive card to wrapper
	 * @param id - adaptive card id
	 * @param feedElement - feed element
	 */
	appendFeedToWrapper(id: string, feedElement: Element): void {
		setTimeout(() => {
			const doc = document.getElementById(id);
			if (doc !== undefined && doc !== null) {
				doc.appendChild(feedElement);
			}
		}, 0);
	}

	/**
	 * Check if template has Image Component
	 * for dynamic card image rendering
	 * @param template - Adaptive Card Template
	 * @returns boolean - true if Template has renderable image else false
	 */
	hasImageInTemplate(template: any): boolean {
		let renderTemplate = template.template ? template.template : '';
		renderTemplate = (renderTemplate.templateJson ? renderTemplate.templateJson : '') as string;
		if (renderTemplate && renderTemplate) {
			renderTemplate = atob(renderTemplate);
			if (renderTemplate.includes('"type": "Image"')) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Update Redered Adaptive Card for Images to be loaded dynamically
	 * as S3 Bucket is private and also return Updated Element
	 * @param feedElement - Adaptive Card HTML Element
	 * @returns feedElement - updated with image loading
	 */
	updateImageCardBeforeRender(feedElement: Element, imageURL: string): Element {
		if (!feedElement.querySelector('.ac-image')) {
			/** If no image div is rendered then return element as it is */
			return feedElement;
		}
		/** Else continue with replacing content with loading animation */
		Array.from(feedElement.querySelectorAll('.ac-container, .ac-action-wrapper')).forEach((node: Element) => {
			if (node.querySelectorAll('*').length) {
				node.classList.add('is-loading');
			}
		});
		/** Then start loading of images Asynchroneously */
		this.s3Service.getImageAsBytes(imageURL).then(
			(url: string) => {
				/** Update image on Successful call */
				feedElement.querySelector('img').src = url;
				/** Remove loading animation */
				setTimeout(() => {
					Array.from(feedElement.querySelectorAll('.is-loading')).forEach((node: Element) =>
						node.classList.remove('is-loading')
					);
				}, 1);
			},
			error => {
				console.error(`[${FeedsPanelComponent.name}][${this.updateImageCardBeforeRender.name}]`, error);
				/** Update card to show image error message */ setTimeout(() => {
					Array.from(feedElement.querySelectorAll('.is-loading')).forEach((node: Element) => {
						if (node.querySelector('.ac-image')) {
							node?.setAttribute('error', 'Unable to load image');
							node.classList.add('error');
						} else {
							node.classList.remove('is-loading');
						}
					});
				}, 1);
			}
		);
		return feedElement;
	}

	/**
	 * Adding wrapper to feed date
	 * @param feedWrapper - feed wrapper
	 * @param feedType -feed type -> here newfeed is processed
	 */
	addFeedWrapper(feedWrapper: any, feedType: any) {
		const self = this;
		setTimeout(() => {
			// Adding Feed Wrapper to DOM first
			if (feedType === 'newsFeed') {
				self.newsFeedResponses.push({
					content: self.sanitizer.bypassSecurityTrustHtml(feedWrapper),
				});
			}
		}, 1);
	}

	/**
	 * Close the pdf displayed for product update feed
	 */
	closePdf() {
		this.showPDF = false;
		document.body.classList.remove('no-scroll');
		// this.feedResponses = [];
		// this.setProfileInformation();
		// this.setupAdaptiveCards();
	}

	/**
	 * To stop loader when pdf is rendered
	 * @param e - event
	 */
	renderPage(e: any): void {
		this.spinner.stopLoader();
	}

	/**
	 * To log error when any error occurs on rendering pdf
	 * @param e - event
	 */
	errorCallback(e: any) {
		console.error(`[${FeedsPanelComponent.name}]`, 'PDF Error', e);
	}

	/**
	 * On component destruction
	 */
}
